/**
 * Feature Utilities
 * Helper functions for working with feature trees
 */

import { DYNAMIC_SEGMENT_MAPPING } from './constants';

/**
 * Flatten feature tree for access checking
 * @param {Array} features - Feature tree from API (with node/children structure)
 * @returns {Object} Map of feature handles to feature objects
 */
export const flattenFeatures = (features) => {
  const featureMap = {};

  const flatten = (featureList) => {
    featureList.forEach(featureWrapper => {
      // Extract the actual feature from the wrapper
      const feature = featureWrapper.node || featureWrapper;
      const children = featureWrapper.children || [];
      
      featureMap[feature.handle] = feature;
      if (children.length > 0) {
        flatten(children);
      }
    });
  };

  flatten(features);
  return featureMap;
};

/**
 * Get route path for a feature (handles dynamic segments)
 * @param {string} featureHandle - Feature handle
 * @param {Object} featureMap - Flattened feature map
 * @param {string} dynamicValue - Value for dynamic segment (e.g., 'documents')
 * @returns {string} Full route path
 */
export const getFeaturePath = (featureHandle, featureMap, dynamicValue = null) => {
  const feature = featureMap[featureHandle];
  if (!feature) return null;

  let path = '/admin';
  
  // Build path by traversing up the tree
  const buildPath = (f) => {
    if (f.parentHandle) {
      const parent = featureMap[f.parentHandle];
      if (parent) {
        buildPath(parent);
        
        // Check if parent uses dynamic segments
        if (DYNAMIC_SEGMENT_MAPPING[parent.handle]) {
          path += `/${parent.handle}/${dynamicValue || f.handle}`;
        } else {
          path += `/${parent.handle}`;
        }
      }
    } else if (DYNAMIC_SEGMENT_MAPPING[f.handle]) {
      path += `/${f.handle}/${dynamicValue || featureHandle}`;
    } else {
      path += `/${f.handle}`;
    }
  };

  buildPath(feature);
  return path;
};

/**
 * File-path segment of a feature's page: children of a dynamic-segment
 * parent (library, taxonomy, reports) share that parent's [segment] page.
 */
export const getFeatureFilePath = (feature, parentHandle) => {
  const segmentName = DYNAMIC_SEGMENT_MAPPING[parentHandle];
  return segmentName ? `[${segmentName}]` : feature.handle;
};

/**
 * Page-component path (relative to src/pages, e.g. "admin/miscellaneous/apps")
 * for a feature under `parentPath` (its parent's URL path, e.g.
 * "/admin/miscellaneous"). The single source for both the router and the side
 * nav, so a nav item only links to a page the router actually renders.
 */
export const getFeatureComponentPath = (parentPath, feature, parentHandle = null) => {
  const cleanPath = parentPath.startsWith('/') ? parentPath.slice(1) : parentPath;
  return `${cleanPath}/${getFeatureFilePath(feature, parentHandle)}`;
};
